import { isLlmOn } from '@/features/llm/isLlmOn';
import { PROFILE_ID } from '@/features/profile/useProfile';
import { db } from '@/lib/db';
import { isSupabaseConfigured, supabase } from '@/lib/supabase';
import { isFaithfulRewrite } from '../../../supabase/functions/llm/guard';
import { mealItemsSchema, type MealItem } from './parseMeal';

/**
 * Cliente de la capa LLM opcional (SPEC 8).
 *
 * Contrato: NUNCA lanza. Cualquier cosa que no sea una respuesta válida —sin
 * servidor, sin clave, límite agotado, proveedor caído, respuesta mal formada,
 * más de 8 segundos— se convierte en `{ available: false }` y la app sigue con
 * sus plantillas. El usuario no tiene por qué enterarse de que existía.
 */

export type LlmKind = 'parse_meal' | 'rewrite_advice';

export type LlmOutcome<T> = { available: true; value: T } | { available: false; reason: string };

/** Lo que devuelve la Edge Function, sin validar todavía. */
export interface RawResponse {
  available?: boolean;
  reason?: string;
  result?: unknown;
}

/** Quien hace la llamada de verdad. Inyectable para poder probarlo sin red. */
export type Invoker = (payload: { kind: LlmKind; input: string }) => Promise<RawResponse>;

const TIMEOUT_MS = 8_000;
const MAX_INPUT = 500;

/** ¿Ha activado el usuario la capa Y ha dado su consentimiento? */
async function consentGiven(): Promise<boolean> {
  try {
    const profile = await db.profile.get(PROFILE_ID);
    return profile != null && profile.deletedAt === null && isLlmOn(profile);
  } catch {
    // Sin poder comprobarlo, no se envía nada. Fallar cerrado.
    return false;
  }
}

/**
 * Lo mínimo que sale del dispositivo (SPEC 8, privacidad).
 *
 * El texto se recorta y se le quita cualquier cosa con pinta de email o de
 * teléfono: es un campo para describir comida, pero la gente escribe de todo, y
 * lo que no hace falta enviar no se envía.
 */
export function sanitizeInput(text: string): string {
  return text
    .replace(/[\w.+-]+@[\w-]+\.[\w.-]+/g, '[email]')
    .replace(/(?:\+?\d[\s-]?){9,}/g, '[teléfono]')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, MAX_INPUT);
}

const defaultInvoker: Invoker = async (payload) => {
  if (!supabase) return { available: false, reason: 'sin_servidor' };
  const invoked = await supabase.functions.invoke<RawResponse>('llm', { body: payload });
  // `error` viene tipado como any en el SDK: basta con saber si existe.
  if (invoked.error != null) return { available: false, reason: 'error_funcion' };
  return invoked.data ?? { available: false, reason: 'respuesta_vacia' };
};

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | 'timeout'> {
  return Promise.race([
    promise,
    new Promise<'timeout'>((resolve) => setTimeout(() => resolve('timeout'), ms)),
  ]);
}

async function call(
  kind: LlmKind,
  input: string,
  invoke: Invoker,
  configured: boolean,
  allowed: () => Promise<boolean>,
): Promise<LlmOutcome<unknown>> {
  if (!configured) return { available: false, reason: 'sin_servidor' };

  // Segunda cerradura, en el único sitio por el que se sale hacia el
  // proveedor. La primera está en la interfaz (`isLlmOn`), pero una puerta que
  // depende de que TODAS las pantallas se acuerden de mirarla es una puerta que
  // un día se queda abierta: esto es lo que saca texto de la UE, y decide él.
  if (!(await allowed())) return { available: false, reason: 'sin_consentimiento' };

  const clean = sanitizeInput(input);
  if (clean.length === 0) return { available: false, reason: 'entrada_vacia' };

  try {
    // Solo `kind` e `input`: ni id de usuario, ni nombre, ni email. La función
    // ya sabe quién llama por el token de sesión, que no forma parte del texto
    // que se reenvía al proveedor.
    const response = await withTimeout(invoke({ kind, input: clean }), TIMEOUT_MS);
    if (response === 'timeout') return { available: false, reason: 'timeout' };
    if (!response.available)
      return { available: false, reason: response.reason ?? 'no_disponible' };
    return { available: true, value: response.result };
  } catch {
    return { available: false, reason: 'error' };
  }
}

/** Parte una frase en alimentos. Solo nombres y cantidades: ningún nutriente. */
export async function parseMealText(
  text: string,
  invoke: Invoker = defaultInvoker,
  configured = isSupabaseConfigured,
  allowed: () => Promise<boolean> = consentGiven,
): Promise<LlmOutcome<MealItem[]>> {
  const outcome = await call('parse_meal', text, invoke, configured, allowed);
  if (!outcome.available) return outcome;

  // El LLM puede cumplir el esquema de Groq y aun así decir algo sin sentido.
  // Se valida otra vez aquí, con nuestras reglas, antes de creerse nada.
  const value = outcome.value as { items?: unknown } | unknown[] | null;
  const items = Array.isArray(value) ? value : value?.items;
  const parsed = mealItemsSchema.safeParse(items);

  return parsed.success
    ? { available: true, value: parsed.data }
    : { available: false, reason: 'respuesta_invalida' };
}

/**
 * Reescribe en lenguaje natural un aviso YA calculado por el motor.
 *
 * Si el LLM quita, cambia o añade una cifra, o le da la vuelta al sentido, se
 * descarta la reescritura y se queda la plantilla: el motor es la única fuente
 * de números y de recomendaciones de la app.
 */
export async function rewriteAdvice(
  text: string,
  invoke: Invoker = defaultInvoker,
  configured = isSupabaseConfigured,
  allowed: () => Promise<boolean> = consentGiven,
): Promise<LlmOutcome<string>> {
  const outcome = await call('rewrite_advice', text, invoke, configured, allowed);
  if (!outcome.available) return outcome;

  const rewritten = typeof outcome.value === 'string' ? outcome.value.trim() : '';
  if (!rewritten) return { available: false, reason: 'respuesta_invalida' };

  // La misma comprobación que hace la función antes de cachear (guard.ts).
  if (!isFaithfulRewrite(text, rewritten)) return { available: false, reason: 'cifras_alteradas' };
  return { available: true, value: rewritten };
}
